import { MAJOR_ARCANA } from '@lunary/tarot-data'
import { toRoman } from './roman'
import { formatDuration } from './format-duration'
import { ORBIT_READING_LABEL } from './orbit-guide'

export type ChatMessage = {
  readonly id: string
  readonly author_role: string
  readonly kind: string
  readonly body: string | null
  readonly audio_url: string | null
  readonly duration_ms: number | null
  readonly peaks: number[] | null
  readonly cards: number[]
  readonly created_at: string
  readonly heard_at: string | null
  readonly author_avatar_url?: string | null
  readonly user_reaction?: number | null
  readonly expert_reaction?: number | null
}

export function reactionsFor(message: ChatMessage, viewer: 'user' | 'expert'): { mine: number | null; other: number | null } {
  const user = message.user_reaction ?? null
  const expert = message.expert_reaction ?? null
  return viewer === 'user' ? { mine: user, other: expert } : { mine: expert, other: user }
}

export type ChatReading = {
  readonly id: string
  readonly status: string
  readonly topic: string | null
  readonly topic_title: string
  readonly clarifications_left?: number
  readonly close_offered?: boolean
  readonly format?: string
  readonly topic_titles?: readonly string[]
  readonly upgrade_offered?: boolean
  readonly requested_at: string
  readonly last_message_at: string
  readonly unheard: number
  readonly display_status?: string | null
  readonly paid_with?: string
  readonly last_message: { readonly author_role: string; readonly kind: string; readonly body: string | null; readonly duration_ms: number | null } | null
}

export type ChatViewer = 'user' | 'expert'

export type ChatPerson = { readonly name: string | null; readonly avatarUrl: string | null }

export function expertAvatar(messages: readonly ChatMessage[]): string | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i]
    if (m && m.author_role === 'expert' && m.author_avatar_url) return m.author_avatar_url
  }
  return null
}

export const READING_STATUS: Readonly<Record<string, { label: string; tone: 'new' | 'work' | 'ready' | 'wait' | 'off' }>> = {
  ordered: { label: 'Оплачен', tone: 'new' },
  pending: { label: 'В очереди', tone: 'new' },
  in_progress: { label: 'Записывается', tone: 'work' },
  ready: { label: 'Готов', tone: 'ready' },
  clarifying: { label: 'Ждёт ответа', tone: 'new' },
  closed: { label: 'Завершён', tone: 'wait' },
  rejected: { label: 'Отклонён', tone: 'off' },
}

export const ACTIVE_READING_STATUSES: readonly string[] = ['ordered', 'pending', 'in_progress', 'ready', 'clarifying']

export function isActiveReading(r: ChatReading): boolean {
  return ACTIVE_READING_STATUSES.includes(r.status)
}

export const READING_FORMAT_LABEL: Readonly<Record<string, string>> = {
  question: 'Один вопрос',
  consultation: 'Консультация',
}

export function readingTopicsLabel(r: ChatReading): string {
  return r.topic_titles && r.topic_titles.length > 0 ? r.topic_titles.join(', ') : r.topic_title
}

export function cardName(n: number): string {
  return MAJOR_ARCANA.find((c) => c.number === n)?.name ?? toRoman(n)
}

export function cardLabel(n: number): string {
  const card = MAJOR_ARCANA.find((c) => c.number === n)
  return card ? `${toRoman(n)} ${card.name}` : toRoman(n)
}

const TIME = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' })
const DAY = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' })
const SHORT = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' })

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

export function timeLabel(iso: string): string {
  return TIME.format(new Date(iso))
}

export function dayLabel(iso: string, now = new Date()): string {
  const d = new Date(iso)
  if (sameDay(d, now)) return 'Сегодня'
  const y = new Date(now)
  y.setDate(y.getDate() - 1)
  if (sameDay(d, y)) return 'Вчера'
  return DAY.format(d)
}

export function listTimeLabel(iso: string, now = new Date()): string {
  const d = new Date(iso)
  return sameDay(d, now) ? TIME.format(d) : SHORT.format(d).replace('.', '')
}

export function mainAnswer(messages: readonly ChatMessage[]): ChatMessage | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m && m.author_role === 'expert' && m.kind === 'voice') return m
  }
  return messages.find((m) => m.author_role === 'expert' && m.kind === 'text') ?? null
}

export type ReadingRowState = {
  readonly tone: 'unread' | 'closing' | 'wait' | 'plain' | 'past'
  readonly count: number
  readonly waitIcon: 'hourglass' | 'pen' | 'question' | null
  readonly label: string
  readonly recording: boolean
}

const ROW_LABEL: Readonly<Record<string, string>> = ORBIT_READING_LABEL

const WAIT_ICON: Readonly<Record<string, 'hourglass' | 'pen' | 'question'>> = {
  pending: 'hourglass',
  in_progress: 'pen',
  clarifying: 'question',
}

export function readingNeedsTopic(r: Pick<ChatReading, 'status' | 'format' | 'topic'>): boolean {
  return r.status === 'ordered' && r.format !== 'consultation' && !r.topic
}

export function readingRowState(r: ChatReading): ReadingRowState {
  if (r.status === 'ordered') {
    const label = readingNeedsTopic(r) ? 'Оплачен · выберите тему' : 'Оплачен · расскажите, что волнует'
    return { tone: 'closing', count: 0, waitIcon: null, label, recording: false }
  }
  const status = r.display_status ?? (r.status === 'ready' ? (r.unheard > 0 ? 'ready' : 'answered') : r.status)
  const waitIcon = WAIT_ICON[status] ?? null
  const label = ROW_LABEL[status] ?? READING_STATUS[r.status]?.label ?? ''
  const tone = r.unheard > 0 ? 'unread' : status === 'closing' ? 'closing' : waitIcon ? 'wait' : isActiveReading(r) ? 'plain' : 'past'
  return { tone, count: r.unheard, waitIcon, label, recording: status === 'in_progress' && r.unheard === 0 }
}

export function previewText(r: ChatReading, viewer: ChatViewer): string {
  const last = r.last_message
  if (!last && r.status === 'ordered') return 'Людмила ждёт вашего рассказа'
  if (!last) return 'Запрос без текста'
  const who = last.author_role === viewer ? 'Вы: ' : ''
  if (last.kind === 'voice') return `${who}Голосовое${last.duration_ms ? ` · ${formatDuration(last.duration_ms)}` : ''}`
  return `${who}${last.body ?? ''}`
}
